import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";

const GlossaryContext = createContext({ terms: [], regex: null, ready: false });

export const GlossaryProvider = ({ children }) => {
  const [terms, setTerms] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancel = false;
    api
      .get("/glossary")
      .then((r) => {
        if (cancel) return;
        const items = (r.data.items || []).map((i) => ({
          term: i.term,
          slug: i.slug,
          summary: i.summary,
        }));
        // Sort longest-first so multi-word matches win over substrings (e.g. "Head and shoulders" before "shoulders")
        items.sort((a, b) => b.term.length - a.term.length);
        setTerms(items);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancel) setReady(true);
      });
    return () => {
      cancel = true;
    };
  }, []);

  // Build a single case-insensitive regex with all known terms, escaped + anchored on word boundaries.
  // We deliberately exclude very short ambiguous tokens (RSI is 3 chars and OK; M, A are not).
  const regex = useMemo(() => {
    if (terms.length === 0) return null;
    const escapes = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const list = terms
      .map((t) => t.term)
      .filter((t) => t && t.length >= 3) // safety: avoid matching 1-2 char abbreviations
      .map(escapes);
    if (list.length === 0) return null;
    return new RegExp(`\\b(${list.join("|")})\\b`, "gi");
  }, [terms]);

  return (
    <GlossaryContext.Provider value={{ terms, regex, ready }}>
      {children}
    </GlossaryContext.Provider>
  );
};

export const useGlossary = () => useContext(GlossaryContext);
